import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

export default function ImpactMetrics({ API_URL, getAuthHeader, refreshKey }) {
  const [metrics, setMetrics] = useState([]);
  const [values, setValues] = useState({});
  const [targets, setTargets] = useState([]);
  const [editMetric, setEditMetric] = useState(null);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [tForm, setTForm] = useState({ metric_key: '', label: '', start: '', end: '', target_value: '', notes: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [mr, vr, tr] = await Promise.all([
        fetch(`${API_URL}/api/impact/metrics`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/metrics/values`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/targets`, { headers: getAuthHeader() }),
      ]);
      if (mr.ok) setMetrics(await mr.json());
      if (vr.ok) setValues((await vr.json()).values || {});
      if (tr.ok) setTargets(await tr.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const saveMetric = async () => {
    const r = await fetch(`${API_URL}/api/impact/metrics/${editMetric.key}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ owner: editMetric.owner, notes: editMetric.notes, definition: editMetric.definition })
    });
    if (r.ok) { toast.success('Metric updated'); setEditMetric(null); fetchAll(); }
    else toast.error('Update failed');
  };

  const createTarget = async () => {
    if (!tForm.metric_key || !tForm.label || !tForm.start || !tForm.end || !tForm.target_value) return toast.error('All fields except notes are required');
    const r = await fetch(`${API_URL}/api/impact/targets`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...tForm, target_value: parseFloat(tForm.target_value) })
    });
    if (r.ok) { toast.success('Target added'); setShowTargetForm(false); setTForm({ metric_key: '', label: '', start: '', end: '', target_value: '', notes: '' }); fetchAll(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Could not add target'); }
  };

  const deleteTarget = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    const r = await fetch(`${API_URL}/api/impact/targets/${id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Target deleted'); fetchAll(); }
  };

  return (
    <div className="space-y-6" data-testid="impact-metrics">
      {/* Targets */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Annual targets vs actuals</h3>
          <button onClick={() => setShowTargetForm(f => !f)} data-testid="impact-add-target-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: BRAND.teal }}>
            <Plus className="w-3.5 h-3.5" /> Add target
          </button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Actuals are calculated live from the same shared layer as the Overview — no separate spreadsheet math.</p>

        {showTargetForm && (
          <div className="grid md:grid-cols-3 gap-3 border border-slate-200 rounded-lg p-4 mb-4" data-testid="impact-target-form">
            <select value={tForm.metric_key} onChange={e => setTForm(f => ({ ...f, metric_key: e.target.value }))}
              data-testid="impact-target-metric" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Metric *</option>
              {metrics.map(m => <option key={m.key} value={m.key}>{m.name}</option>)}
            </select>
            <input placeholder="Label (e.g. FY2026) *" value={tForm.label} onChange={e => setTForm(f => ({ ...f, label: e.target.value }))}
              data-testid="impact-target-label" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input type="number" placeholder="Target value *" value={tForm.target_value} onChange={e => setTForm(f => ({ ...f, target_value: e.target.value }))}
              data-testid="impact-target-value" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input type="date" value={tForm.start} onChange={e => setTForm(f => ({ ...f, start: e.target.value }))} data-testid="impact-target-start" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input type="date" value={tForm.end} onChange={e => setTForm(f => ({ ...f, end: e.target.value }))} data-testid="impact-target-end" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Notes" value={tForm.notes} onChange={e => setTForm(f => ({ ...f, notes: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2 md:col-span-3">
              <button onClick={createTarget} data-testid="impact-target-save" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.navy }}>Save target</button>
              <button onClick={() => setShowTargetForm(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
            </div>
          </div>
        )}

        {targets.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No targets set. Add goals like “300 unique people engaged in FY2026” to track progress automatically.</p>
        ) : (
          <div className="space-y-3">
            {targets.map(t => (
              <div key={t.id} className="border border-slate-200 rounded-lg p-4" data-testid={`impact-target-${t.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-bold" style={{ color: BRAND.navy }}>{t.metric_name} — {t.label}</p>
                    <p className="text-xs text-slate-400">{t.start} → {t.end}{t.notes ? ` · ${t.notes}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold" style={{ color: BRAND.navy }}>
                      {t.actual ?? '—'} / {t.target_value}
                      {t.progress_pct !== null && <span className="ml-2 text-xs font-semibold" style={{ color: t.progress_pct >= 100 ? '#4d7c0f' : BRAND.teal }}>{t.progress_pct}%</span>}
                    </span>
                    <button onClick={() => deleteTarget(t.id)} data-testid={`impact-target-delete-${t.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, t.progress_pct || 0)}%`, background: (t.progress_pct || 0) >= 100 ? BRAND.lime : BRAND.teal }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Metric dictionary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Metric dictionary</h3>
        <p className="text-xs text-slate-400 mb-4">One definition per metric, used identically across all screens and future reports. Current all-time values shown for reference.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="impact-metrics-table">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
              <th className="py-2.5 px-3">Metric</th><th className="px-2">Current value</th><th className="px-2">Definition</th><th className="px-2">Date basis</th><th className="px-2">Aggregation</th><th className="px-2">Owner</th><th></th>
            </tr></thead>
            <tbody>
              {metrics.map(m => (
                <tr key={m.key} className="border-b border-slate-100 align-top" data-testid={`impact-metric-${m.key}`}>
                  <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: BRAND.navy }}>{m.name}<div className="text-[10px] font-normal text-slate-400">{m.unit}</div></td>
                  <td className="px-2 font-bold" style={{ color: BRAND.teal }}>{values[m.key] ?? '—'}</td>
                  <td className="px-2 text-xs text-slate-500 max-w-md">{m.definition}{m.notes && <div className="mt-1 italic text-slate-400">Note: {m.notes}</div>}</td>
                  <td className="px-2 text-xs text-slate-500">{m.date_basis}</td>
                  <td className="px-2 text-xs text-slate-500">{m.aggregation}</td>
                  <td className="px-2 text-xs">{m.owner || <span className="text-slate-300">—</span>}</td>
                  <td className="px-2"><button onClick={() => setEditMetric({ ...m })} data-testid={`impact-metric-edit-${m.key}`} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editMetric && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditMetric(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="impact-metric-edit-modal">
            <h3 className="font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>{editMetric.name}</h3>
            <label className="text-xs font-semibold text-slate-500">Definition</label>
            <textarea value={editMetric.definition} rows={4} onChange={e => setEditMetric(m => ({ ...m, definition: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <label className="text-xs font-semibold text-slate-500">Owner (responsible staff)</label>
            <input value={editMetric.owner || ''} onChange={e => setEditMetric(m => ({ ...m, owner: e.target.value }))} data-testid="impact-metric-owner-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <label className="text-xs font-semibold text-slate-500">Notes</label>
            <input value={editMetric.notes || ''} onChange={e => setEditMetric(m => ({ ...m, notes: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2 pt-1">
              <button onClick={saveMetric} data-testid="impact-metric-save" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.teal }}>Save</button>
              <button onClick={() => setEditMetric(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
